import { monthIndexToMonth } from 'api/recommendations'
import moment from 'moment'
import type { IntlShape } from 'react-intl'

interface Props {
  checkIn?: string
  monthIndex?: string
  duration: number
  intl: IntlShape
}

export function getRecommendationsTitle({
  checkIn,
  monthIndex,
  duration,
  intl,
}: Props): { title: string, subtitle?: string } {
  if (checkIn) {
    const displayFormat = 'D MMM'
    const from = moment(checkIn).format(displayFormat)
    const to = moment(checkIn).add(duration, 'days').format(displayFormat)
    const title = intl.formatMessage({
      id: '0xsbWhF4Y_3Pi1rl3T4X',
      defaultMessage: 'Best offers for your dates',
      description: 'Calendar recommendations title when specific check-in date is selected',
    })
    const subtitle = intl.formatMessage(
      {
        id: 'A3-jaw2DfewAGn9WO_NG',
        defaultMessage: '{from} - {to} ({duration, plural, one {# night} other {# nights}})',
        description: 'Calendar recommendations subtitle showing date range and duration',
      },
      { from, to, duration },
    )
    return {
      title,
      subtitle,
    }
  }
  if (monthIndex) {
    return {
      title: intl.formatMessage(
        {
          id: 'UIwl8apHWADOKZjbnWWo',
          defaultMessage: 'Check our best selling hotels available in {month}',
          description: 'Calendar recommendations title when a month is selected',
        },
        { month: moment(monthIndexToMonth(monthIndex), 'M-YYYY').format('MMMM') },
      ),
    }
  }
  return {
    title: intl.formatMessage({
      id: 'SXwJ0VS3HTS96qG4unlo',
      defaultMessage: 'Check our best selling hotels',
      description: 'Default calendar recommendations title on the hotel or room booking page',
    }),
  }
}

interface MobileProps {
  checkIn?: string
  monthIndex?: string
  duration: number
  intl: IntlShape
  variant?: 'available' | 'default'
}

export function getRecommendationsTitleMobile({
  checkIn,
  monthIndex,
  duration,
  intl,
  variant = 'default',
}: MobileProps): string {
  if (checkIn) {
    const displayFormat = 'DD MMM'
    const from = moment(checkIn).format(displayFormat)
    const to = moment(checkIn).add(duration, 'days').format(displayFormat)
    if (variant === 'available') {
      return intl.formatMessage(
        {
          id: 'PoL1-yNNvVR0Wlt1EOk3',
          defaultMessage: 'Available for {from} - {to}',
          description: 'Mobile calendar recommendations title showing availability date range',
        },
        { from, to },
      )
    }
    return intl.formatMessage(
      {
        id: 'K843VOiDeNUqerCEZe-Y',
        defaultMessage: '{from} - {to}',
        description: 'Mobile calendar recommendations title showing date range',
      },
      { from, to },
    )
  }
  if (monthIndex) {
    const month = moment(monthIndexToMonth(monthIndex), 'M-YYYY').format('MMMM')
    if (variant === 'available') {
      return intl.formatMessage(
        {
          id: '-sZV8lQkpVYAy9yT38DJ',
          defaultMessage: 'Available in {month}',
          description: 'Mobile calendar recommendations title showing availability month',
        },
        { month },
      )
    }
    return intl.formatMessage(
      {
        id: 'Lw-BL9NMlT_PRkMznAx4',
        defaultMessage: 'Check our best selling hotels available in {month}',
        description: 'Mobile calendar recommendations title when a month is selected',
      },
      { month },
    )
  }
  if (variant === 'available') {
    return intl.formatMessage({
      id: '1pxp3IiQxMNwzzJOowsv',
      defaultMessage: 'Similar hotels',
      description: 'Default mobile recommendations title for similar hotels',
    })
  }
  return intl.formatMessage({
    id: 'Q64WZaVzNNXWFiGnKafW',
    defaultMessage: 'Check our best selling hotels',
    description: 'Default mobile calendar recommendations title on the offer detail page',
  })
}
